/**
 * The optics bench's drawing: the rail, the grid, the parts, the rays and the
 * images — and the gestures on them (move, turn, pan, zoom).
 *
 * Everything drawn is read FROM the engine; nothing here is measured and fed
 * back. The one direction the other way is a drag, which writes a snapped,
 * round number into the world through `onMove`.
 *
 * Bench coordinates are centimetres with y up. The SVG is given them directly,
 * y negated once in `S()`, so a centimetre is the same size across and up —
 * the equal scale the engine's angles depend on. Strokes are set in SCREEN
 * pixels (`non-scaling-stroke`) and text is sized from the scale, so a zoom
 * changes how much bench is shown and never how thick a ray is.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { optics } from '@padcanvas/shared';
import type { optics as O } from '@padcanvas/shared';
import {
    RAY_SWEEP_MS, gridFor, railTicks, snapAngle, snapDrag, truncatePath, visibleLength,
    type SnapResult, type SnapTarget, type View, zoomAbout,
} from './view';
import { rodColour, wavelengthInk, whiteInk } from './palette';

const { defOf, objectPoint, fromAngle, add, scale, sub, dist, unit, perp, sag } = optics;

type Vec2 = O.Vec2;

export interface SceneProps {
    world: O.World;
    analysis: O.Analysis;
    view: View;
    onView: (v: View) => void;
    onSize: (size: { w: number; h: number }) => void;
    selectedId: string | null;
    onSelect: (id: string | null) => void;
    /** Called once as a drag begins, so the whole drag is one step of undo. */
    onDragStart: () => void;
    onMove: (id: string, at: Vec2) => void;
    onTurn: (id: string, angle: number) => void;
    /** Bring this bench point into view — an edge tag was clicked. */
    onReveal: (p: Vec2) => void;
    showGrid: boolean;
    snap: boolean;
    showAngles: boolean;
    /** Draw heights this many times their true scale (1: true scale). Only for
     *  an arrangement that is all on the rail, where an image's position, size
     *  and nature do not depend on it — the host decides, and says so on the
     *  bench. Every point is converted through it, both ways. */
    stretchY?: number;
    /** The bench's theme: light darkens the colours of light so they show. */
    theme?: 'light' | 'dark';
}

const CLASS: Record<string, string> = {
    lens: 'glass', prism: 'glass', slab: 'glass', semicircle: 'glass',
    planeMirror: 'mirror', sphericalMirror: 'mirror',
    screen: 'screen', stop: 'stop',
    // A lamp glows; a ray box or beam is a dark housing with a lit slit.
    pointSource: 'source', parallelBeam: 'housing', rayBox: 'housing',
};

type Drag =
    /** `free`: a long press held still before moving lifts snapping for the drag
     *  — the touch screen's Alt. `client` is where the press began. */
    | { mode: 'move'; id: string; offset: Vec2; started: boolean; free: boolean; client: Vec2 }
    | { mode: 'turn'; id: string; started: boolean }
    | { mode: 'pan'; client: Vec2; view: View; moved: boolean }
    /** Two fingers: `dist` and `mid` as they first landed, the view then, and
     *  the bench point under the midpoint — which stays under the fingers. */
    | { mode: 'pinch'; dist: number; mid: Vec2; view: View; anchor: Vec2 };

/** How far an edge tag keeps from the bench's top and foot, px: below the bin
 *  and Play buttons, above the scale and Grid and Snap. */
const EDGE_TOP_PX = 64;
const EDGE_FOOT_PX = 60;

/** How long a finger must rest on a part, still, before its drag goes unsnapped. */
const LONG_PRESS_MS = 450;
/** Movement that counts as "not still" before the long press is up, px. */
const PRESS_SLOP_PX = 6;

/** Lenses and mirrors that have named points: on the rail, with a finite focus. */
const focusing = (world: O.World): O.Part[] => world.parts.filter(p => {
    const kind = defOf(p);
    return !!kind.element && p.mount === 'axis' && Number.isFinite(kind.element(p, world).f);
});

/**
 * Whose F this is, as a suffix: nothing with one element on the bench, the
 * element's number with several of one kind (F₁ is L₁'s), and its whole name
 * when lenses and mirrors are mixed (F(M₁)), where a bare number would be
 * ambiguous. Plus a hue, so each element's marks share a colour with its name.
 */
export function markerKeys(world: O.World): Map<string, { suffix: string; hue: number }> {
    const els = focusing(world);
    const tags = new Set(els.map(p => defOf(p).tag));
    return new Map(els.map((p, i) => {
        const kind = defOf(p);
        const name = kind.name(p, world);
        const suffix = els.length <= 1 ? '' : tags.size === 1 ? name.slice(kind.tag.length) : `(${name})`;
        return [p.id, { suffix, hue: els.length <= 1 ? 0 : i % 4 }];
    }));
}

/** The named points of a lens or mirror: where a drag snaps, and what the rail marks. */
export function namedPoints(world: O.World, part: O.Part, suffix = ''): SnapTarget[] {
    const kind = defOf(part);
    if (!kind.element || part.mount !== 'axis') return [];
    const el = kind.element(part, world);
    if (!Number.isFinite(el.f)) return [];
    const at = (k: number): Vec2 => add(el.centre, scale(el.axis, k * el.f));
    const L = (base: string): string => `${base}${suffix}`;
    if (el.folds) {
        return [{ at: el.centre, label: L('P') }, { at: at(1), label: L('F') }, { at: at(2), label: L('C') }];
    }
    return [
        { at: el.centre, label: L('O') },
        { at: at(1), label: L('F') }, { at: at(-1), label: L('F') },
        { at: at(2), label: L('2F') }, { at: at(-2), label: L('2F') },
    ];
}

export function OpticsScene2D(props: SceneProps): React.JSX.Element {
    const { world, analysis, view, selectedId, showGrid, snap, showAngles } = props;
    /** Light of a wavelength, as drawn on this bench. */
    const wavelengthColour = (nm: number): string => wavelengthInk(nm, props.theme);
    /** A source's own colour — white, for one shining white. */
    const sourceInk = (p: O.Part): string => (optics.isWhite(p) ? whiteInk(props.theme) : wavelengthColour(optics.wavelengthOf(p, world)));
    const wrapRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [size, setSize] = useState({ w: 800, h: 450 });
    const [live, setLive] = useState<{ id: string; snap: SnapResult; client: Vec2 } | null>(null);
    /** How far the light has travelled, cm, while the ray animation plays; null otherwise. */
    const [travel, setTravel] = useState<number | null>(null);
    const frame = useRef<number | null>(null);
    const stopAnimation = (): void => {
        if (frame.current !== null) cancelAnimationFrame(frame.current);
        frame.current = null;
        setTravel(null);
    };
    useEffect(() => () => { if (frame.current !== null) cancelAnimationFrame(frame.current); }, []);
    const drag = useRef<Drag | null>(null);
    /** Every pointer down on the bench, so a second finger can turn a pan or a
     *  drag into a pinch. */
    const pointers = useRef(new Map<number, Vec2>());
    const pressTimer = useRef<number | null>(null);
    const clearPress = (): void => {
        if (pressTimer.current !== null) window.clearTimeout(pressTimer.current);
        pressTimer.current = null;
    };
    useEffect(() => clearPress, []);
    /** Shown on the live label while a long-pressed drag runs unsnapped. */
    const [freeDrag, setFreeDrag] = useState(false);
    const viewRef = useRef(view);
    viewRef.current = view;

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            const w = Math.max(1, entry.contentRect.width), h = Math.max(1, entry.contentRect.height);
            setSize({ w, h });
            props.onSize({ w, h });
        });
        ro.observe(el);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const ky = props.stretchY ?? 1;
    const kyRef = useRef(ky);
    kyRef.current = ky;
    const pxPerCm = size.w / view.width;
    const px = (n: number): number => n / pxPerCm;
    /** How wide a part's tap target is on screen, beyond its own outline. */
    const HIT_PX = 24;
    /** The view's height in drawing units — bench cm, with heights times `ky`. */
    const H = (view.width * size.h) / size.w;
    /** …and in bench cm: a stretched view shows less height. */
    const Hw = H / ky;
    const x0 = view.cx - view.width / 2;
    const yTop = view.cy + Hw / 2;
    const inView = (p: Vec2): boolean => p.x >= x0 && p.x <= x0 + view.width && p.y <= yTop && p.y >= yTop - Hw;
    /** Bench y → SVG y: negated, and stretched. Every drawn y goes through here. */
    const Y = (y: number): number => -y * ky;
    const S = (p: Vec2): string => `${p.x},${Y(p.y)}`;

    const toBench = (clientX: number, clientY: number): Vec2 => {
        const r = (svgRef.current as SVGSVGElement).getBoundingClientRect();
        return { x: x0 + (clientX - r.left) / pxPerCm, y: yTop - (clientY - r.top) / pxPerCm / ky };
    };

    // Wheel zoom about the pointer. Native and non-passive, so the page does not scroll too.
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        const onWheel = (e: WheelEvent): void => {
            e.preventDefault();
            const v = viewRef.current;
            const r = svg.getBoundingClientRect();
            const ppc = r.width / v.width;
            const h = (v.width * r.height) / r.width;
            const k = kyRef.current;
            const anchor = { x: v.cx - v.width / 2 + (e.clientX - r.left) / ppc, y: v.cy + (h / 2 - (e.clientY - r.top) / ppc) / k };
            props.onView(zoomAbout(v, v.width * Math.exp(e.deltaY * 0.0015), anchor));
        };
        svg.addEventListener('wheel', onWheel, { passive: false });
        return () => svg.removeEventListener('wheel', onWheel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const partById = useMemo(() => new Map(world.parts.map(p => [p.id, p])), [world.parts]);
    const keys = useMemo(() => markerKeys(world), [world]);
    const reports = useMemo(() => new Map(analysis.images.map(r => [r.partId, r])), [analysis.images]);

    // Every lens and mirror's named points are snap targets — dragging the object
    // selects the object, and "put it at 2F" must still be one drag.
    const named = (exceptId: string): SnapTarget[] =>
        world.parts.filter(p => p.id !== exceptId).flatMap(p => namedPoints(world, p, keys.get(p.id)?.suffix));

    // ── gestures ──────────────────────────────────────────────────────────────

    /** Note the pointer. A second one down turns whatever was starting into a
     *  pinch, and says so — the caller then starts nothing of its own. */
    const track = (e: React.PointerEvent): boolean => {
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        svgRef.current?.setPointerCapture(e.pointerId);
        if (pointers.current.size !== 2) return pointers.current.size > 2;
        clearPress();
        setLive(null);
        setFreeDrag(false);
        const [a, b] = [...pointers.current.values()];
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const v = viewRef.current;
        drag.current = { mode: 'pinch', dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), mid, view: v, anchor: toBench(mid.x, mid.y) };
        return true;
    };

    const startMove = (e: React.PointerEvent, part: O.Part): void => {
        e.stopPropagation();
        if (track(e)) return;
        props.onSelect(part.id);
        const at = toBench(e.clientX, e.clientY);
        drag.current = { mode: 'move', id: part.id, offset: sub(at, part.at), started: false, free: false, client: { x: e.clientX, y: e.clientY } };
        // A finger has no Alt key: resting on the part before moving it is the same ask.
        if (e.pointerType !== 'mouse') {
            clearPress();
            pressTimer.current = window.setTimeout(() => {
                pressTimer.current = null;
                const d = drag.current;
                if (d?.mode === 'move' && !d.started) {
                    d.free = true;
                    setFreeDrag(true);
                    navigator.vibrate?.(15);
                }
            }, LONG_PRESS_MS);
        }
    };

    const startTurn = (e: React.PointerEvent, part: O.Part): void => {
        e.stopPropagation();
        if (track(e)) return;
        drag.current = { mode: 'turn', id: part.id, started: false };
    };

    const onPointerDown = (e: React.PointerEvent): void => {
        if (track(e)) return;
        drag.current = { mode: 'pan', client: { x: e.clientX, y: e.clientY }, view, moved: false };
    };

    const onPointerMove = (e: React.PointerEvent): void => {
        if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const d = drag.current;
        if (!d) return;
        if (d.mode === 'pinch') {
            const [a, b] = [...pointers.current.values()];
            if (!a || !b) return;
            const r = (svgRef.current as SVGSVGElement).getBoundingClientRect();
            const dist = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            // Zoom about the point first under the fingers, then carry it to
            // where the fingers are now: pinch and pan in one gesture.
            const zoomed = zoomAbout(d.view, d.view.width * (d.dist / dist), d.anchor);
            const ppc = r.width / zoomed.width;
            props.onView({ ...zoomed, cx: zoomed.cx - (mid.x - d.mid.x) / ppc, cy: zoomed.cy + (mid.y - d.mid.y) / ppc / ky });
            return;
        }
        if (d.mode === 'pan') {
            const dx = e.clientX - d.client.x, dy = e.clientY - d.client.y;
            if (!d.moved && Math.hypot(dx, dy) < 3) return;
            d.moved = true;
            props.onView({ ...d.view, cx: d.view.cx - dx / pxPerCm, cy: d.view.cy + dy / pxPerCm / ky });
            return;
        }
        const part = partById.get(d.id);
        if (!part) return;
        if (d.mode === 'move' && !d.started) {
            // Still inside the press's slop: not a drag yet, and the long press may still land.
            if (Math.hypot(e.clientX - d.client.x, e.clientY - d.client.y) < PRESS_SLOP_PX) return;
            clearPress();
        }
        if (!d.started) { d.started = true; props.onDragStart(); }
        const at = toBench(e.clientX, e.clientY);
        if (d.mode === 'move') {
            const others = world.parts.filter(p => p.id !== part.id).map(p => p.at);
            const s = snapDrag(sub(at, d.offset), {
                onAxis: part.mount === 'axis', pxPerCm, named: named(part.id), others, enabled: snap && !e.altKey && !d.free,
            });
            props.onMove(part.id, s.at);
            setLive({ id: part.id, snap: s, client: { x: e.clientX, y: e.clientY } });
        } else {
            const raw = Math.atan2(at.y - part.at.y, at.x - part.at.x);
            props.onTurn(part.id, snapAngle(raw, pxPerCm, snap && !e.altKey));
        }
    };

    const onPointerUp = (e: React.PointerEvent): void => {
        pointers.current.delete(e.pointerId);
        const d = drag.current;
        // A pinch ends when either finger lifts; the one left does nothing until
        // it lifts too, rather than jumping into a pan from mid-gesture.
        if (d?.mode === 'pinch') {
            if (pointers.current.size === 0) drag.current = null;
            return;
        }
        clearPress();
        if (d?.mode === 'pan' && !d.moved) props.onSelect(null);
        drag.current = null;
        setLive(null);
        setFreeDrag(false);
    };

    const onKeyDown = (e: React.KeyboardEvent): void => {
        const step = view.width * 0.1;
        const move: Record<string, Vec2> = { ArrowLeft: { x: -step, y: 0 }, ArrowRight: { x: step, y: 0 }, ArrowUp: { x: 0, y: step }, ArrowDown: { x: 0, y: -step } };
        const m = move[e.key];
        if (!m) return;
        e.preventDefault();
        props.onView({ ...view, cx: view.cx + m.x, cy: view.cy + m.y });
    };

    // ── what to draw ──────────────────────────────────────────────────────────

    const grid = gridFor(pxPerCm);
    const gridLines = useMemo(() => {
        if (!showGrid) return null;
        const lines = (step: number): string => {
            let d = '';
            const xa = Math.floor(x0 / step) * step, ya = Math.floor((yTop - Hw) / step) * step;
            for (let x = xa; x <= x0 + view.width; x += step) d += `M${x},${-yTop * ky}V${-(yTop - Hw) * ky}`;
            // Stretched, the rows spread with the heights: the grid shows the stretch.
            for (let y = ya; y <= yTop; y += step) d += `M${x0},${-y * ky}H${x0 + view.width}`;
            return d;
        };
        return { minor: lines(grid.minor), major: lines(grid.major) };
    }, [showGrid, grid.minor, grid.major, x0, yTop, Hw, ky, view.width]);

    const selected = selectedId ? partById.get(selectedId) : undefined;
    // F, 2F, C marked for the selected lens or mirror; with none selected, for every one.
    const markerParts = selected && defOf(selected).element ? [selected] : world.parts.filter(p => defOf(p).element);
    const markers = markerParts.flatMap(p => {
        const key = keys.get(p.id);
        return namedPoints(world, p, key?.suffix)
            .filter(t => !t.label.startsWith('O') && !t.label.startsWith('P'))
            .map(t => ({ ...t, hue: key?.hue ?? 0 }));
    });
    // Two elements' marks can land on one spot (L₂'s F on L₁'s centre): each
    // label rises above any already there, so neither hides the other.
    const lift = markers.map((m, i) =>
        markers.slice(0, i).filter(o => Math.abs(o.at.x - m.at.x) < px(22) && Math.abs(o.at.y - m.at.y) < px(6)).length);

    const rayColour = (ray: O.Ray): string => {
        const part = partById.get(ray.source.partId);
        if (part?.kind === 'object') {
            const data = part.data as unknown as O.ObjectData;
            if (data.form === 'rod') return rodColour(data.palette, ray.source.t ?? 0);
        }
        return wavelengthColour(ray.wavelength);
    };

    /** The image of the point a ray came from, if that point has one. */
    const imageOfSource = (ray: O.Ray): O.SampledImage | undefined => {
        const report = reports.get(ray.source.partId);
        if (!report) return undefined;
        return report.points.find(p => p.t === (ray.source.t ?? 0)) ?? (report.points.length === 1 ? report.points[0] : undefined);
    };

    const surfaceById = useMemo(() => new Map(analysis.surfaces.map(s => [s.id, s])), [analysis.surfaces]);

    const normalAt = (v: O.Vertex): Vec2 | null => {
        const s = v.surfaceId ? surfaceById.get(v.surfaceId) : undefined;
        if (!s) return null;
        if (s.shape.form === 'arc') return unit(sub(v.at, s.shape.centre));
        return unit(perp(sub(s.shape.b, s.shape.a)));
    };

    /**
     * The textbook's mark for the side of a mirror that does not reflect: short
     * slanted strokes along its back. Without it a mirror facing away from the
     * light looks identical to one facing it, and the rays simply stop.
     */
    const mirrorHatch = (p: O.Part): string => {
        const spherical = p.kind === 'sphericalMirror';
        const d = p.data as Record<string, unknown>;
        const half = (spherical ? (d.aperture as number) : (d.length as number)) / 2;
        const curve = spherical ? (d.curve === 'concave' ? 1 : -1) : 0;
        const face = fromAngle(p.angle), across = perp(face);
        const n = Math.max(4, Math.round((2 * half) / px(7)));
        let path = '';
        for (let i = 0; i <= n; i++) {
            const y = -half + (2 * half * i) / n;
            const x = curve ? curve * sag(d.radius as number, y) : 0;
            const back = add(add(p.at, scale(face, x - 0.5)), scale(across, y));
            // The stroke itself in drawing units, so a stretch does not tilt it.
            const faceD = unit({ x: face.x, y: face.y * ky }), acrossD = unit({ x: across.x, y: across.y * ky });
            const bx = back.x, by = Y(back.y);
            const tx = bx - faceD.x * px(7) + acrossD.x * px(5), ty = by + faceD.y * px(7) - acrossD.y * px(5);
            path += `M${bx},${by}L${tx},${ty}`;
        }
        return path;
    };

    /**
     * Where a ray is DRAWN to meet a spherical mirror. The engine reflects at the
     * pole plane — that is what makes the rays meet exactly (engine target §6) —
     * but a ray visibly crossing the silvered arc to reach an invisible line looks
     * wrong. So the incoming line is drawn only as far as the arc, and the
     * reflected line from where IT meets the arc. Both lines are exactly the
     * engine's; only the stretch between the arc and the plane goes, and the two
     * ends are joined along the mirror, under its own stroke.
     */
    const onMirrorArc = (p: O.Part, lineAt: Vec2, dir: Vec2): Vec2 => {
        const d = p.data as Record<string, unknown>;
        const R = d.radius as number;
        const s = d.curve === 'concave' ? 1 : -1;
        const centre = add(p.at, scale(fromAngle(p.angle), s * R));
        const f = sub(lineAt, centre);
        const b = f.x * dir.x + f.y * dir.y;
        const disc = b * b - (f.x * f.x + f.y * f.y - R * R);
        if (disc < 0) return lineAt;
        const r = Math.sqrt(disc);
        const t = Math.abs(-b - r) < Math.abs(-b + r) ? -b - r : -b + r;
        return add(lineAt, scale(dir, t));
    };

    /** A ray's drawn path: its vertices, with each spherical-mirror vertex put on the arc. */
    const drawnPath = (ray: O.Ray): Vec2[] => {
        const out: Vec2[] = [];
        ray.vertices.forEach((v, i) => {
            const part = v.partId ? partById.get(v.partId) : undefined;
            if (i === 0 || part?.kind !== 'sphericalMirror') { out.push(v.at); return; }
            out.push(onMirrorArc(part, v.at, ray.vertices[i - 1].direction));
            if (i < ray.vertices.length - 1) out.push(onMirrorArc(part, v.at, v.direction));
        });
        return out;
    };

    /** A head at `at` pointing along `dir`, shaped in drawing units — a stretch
     *  moves and turns it with its ray, and never squashes it. */
    const arrowHead = (at: Vec2, dir: Vec2, sizePx = 7): string => {
        const a = { x: at.x, y: at.y * ky };
        const d = unit({ x: dir.x, y: dir.y * ky });
        const back = scale(d, -px(sizePx)), side = scale(perp(d), px(sizePx * 0.55));
        // Already in drawing units: negate only, never stretch twice.
        return [a, add(add(a, back), side), add(sub(a, side), back)].map(q => `${q.x},${-q.y}`).join(' ');
    };

    // Edge tags: images and parts that are off the view.
    const tags: { key: string; text: string; at: Vec2; toward: Vec2 }[] = [];
    for (const report of analysis.images) {
        const lead = report.points.find(p => p.t === 1) ?? report.points[0];
        if (!lead) continue;
        if ((lead.kind === 'real' || lead.kind === 'virtual') && !inView(lead.at)) {
            tags.push({ key: `img-${report.partId}`, text: `image at x = ${lead.at.x.toFixed(0)} cm`, at: lead.at, toward: lead.at });
        } else if (lead.kind === 'at-infinity' && lead.direction) {
            tags.push({ key: `inf-${report.partId}`, text: 'image at infinity', at: lead.at, toward: add(lead.at, scale(lead.direction, 1e5)) });
        }
    }
    for (const p of world.parts) {
        if (!inView(p.at)) tags.push({ key: `part-${p.id}`, text: defOf(p).name(p, world), at: p.at, toward: p.at });
    }
    /** Where a tag pointing at `toward` sits on the edge of the panel, in px. */
    const edgeSpot = (toward: Vec2): { left: number; top: number; arrow: string } => {
        const c = { x: view.cx, y: view.cy };
        const d = sub(toward, c);
        const kx = d.x !== 0 ? (view.width / 2 - px(60)) / Math.abs(d.x) : Infinity;
        const kh = d.y !== 0 ? (Hw / 2 - px(24) / ky) / Math.abs(d.y) : Infinity;
        const k = Math.min(kx, kh, 1);
        const q = add(c, scale(d, k));
        const angle = Math.atan2(d.y * ky, d.x);
        const arrow = ['→', '↗', '↑', '↖', '←', '↙', '↓', '↘'][((Math.round(angle / (Math.PI / 4)) % 8) + 8) % 8];
        // Clear of the rows of buttons along the bench's top (bin, Play) and foot
        // (scale, Grid and Snap): a tag pushed under them could not be read or tapped.
        const top = Math.min(Math.max((yTop - q.y) * pxPerCm * ky, EDGE_TOP_PX), size.h - EDGE_FOOT_PX);
        return { left: (q.x - x0) * pxPerCm, top, arrow };
    };

    const fontCm = px(12);

    /**
     * White light's shared stretch: for each white ray's three colours, the
     * vertices they have in common — the same points, leaving in the same
     * direction — up to where a dispersing prism parts them. Until then the
     * three overlap exactly, so drawing them white there is the truth.
     */
    const whiteRuns: { ray: O.Ray; vertices: O.Vertex[] }[] = [];
    {
        const groups = new Map<string, O.Ray[]>();
        for (const r of analysis.rays) {
            if (!r.white) continue;
            const key = `${r.source.partId}|${r.source.point}`;
            groups.set(key, [...(groups.get(key) ?? []), r]);
        }
        const same = (a: Vec2, b: Vec2): boolean => Math.abs(a.x - b.x) < 1e-9 && Math.abs(a.y - b.y) < 1e-9;
        for (const rays of groups.values()) {
            const [first] = rays;
            let k = 0;
            while (k + 1 < first.vertices.length
                && rays.every(r => r.vertices[k + 1] && same(r.vertices[k + 1].at, first.vertices[k + 1].at))
                && rays.every(r => same(r.vertices[k].direction, first.vertices[k].direction))) k++;
            if (k > 0) whiteRuns.push({ ray: first, vertices: first.vertices.slice(0, k + 1) });
        }
    }

    const animating = travel !== null;
    /** Trace the light out from its sources: one view width every RAY_SWEEP_MS. */
    const playRays = (): void => {
        const speed = view.width / RAY_SWEEP_MS;
        const end = Math.max(0, ...analysis.rays.map(r => visibleLength(drawnPath(r), view, size.h / size.w / ky)));
        if (end <= 0) return;
        const t0 = performance.now();
        const tick = (now: number): void => {
            const s = (now - t0) * speed;
            if (s >= end) { frame.current = null; setTravel(null); return; }
            setTravel(s);
            frame.current = requestAnimationFrame(tick);
        };
        setTravel(0);
        frame.current = requestAnimationFrame(tick);
    };

    return (
        <div className="obench-scenewrap" ref={wrapRef}>
            <svg
                ref={svgRef}
                className="obench-scene"
                viewBox={`${x0} ${Y(yTop)} ${view.width} ${H}`}
                preserveAspectRatio="none"
                tabIndex={0}
                role="application"
                aria-label="Optics bench. Drag parts to move them, drag the empty bench to pan, scroll or pinch to zoom."
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onKeyDown={onKeyDown}
                style={{ fontSize: `${fontCm}px` }}
            >
                {gridLines && (
                    <g className="obench-grid">
                        <path d={gridLines.minor} style={{ opacity: grid.minorOpacity }} vectorEffect="non-scaling-stroke" />
                        <path d={gridLines.major} className="major" vectorEffect="non-scaling-stroke" />
                    </g>
                )}

                {/* The rail: the principal axis, its scale, and the zero mark. */}
                <g className="obench-axis">
                    <line x1={world.axis.from} y1={0} x2={world.axis.to} y2={0} vectorEffect="non-scaling-stroke" />
                    <line className="beyond" x1={x0} y1={0} x2={x0 + view.width} y2={0} vectorEffect="non-scaling-stroke" />
                    {railTicks(view, world.axis).map(t => (
                        <g key={t.at}>
                            <line x1={t.at} y1={0} x2={t.at} y2={px(6)} vectorEffect="non-scaling-stroke" />
                            <text x={t.at} y={px(20)} textAnchor="middle">{t.label}</text>
                        </g>
                    ))}
                </g>

                {/* F, 2F, C — how every textbook question names a position. */}
                <g className="obench-markers">
                    {markers.map((m, i) => (
                        <g key={i} data-hue={m.hue}>
                            <circle cx={m.at.x} cy={Y(m.at.y)} r={px(3)} />
                            <text x={m.at.x} y={Y(m.at.y) - px(8 + 13 * lift[i])} textAnchor="middle">{m.label}</text>
                        </g>
                    ))}
                </g>

                {/* Parts, drawn from their own outlines. */}
                {world.parts.filter(p => p.kind !== 'object').map(p => {
                    const outline = defOf(p).outline(p, world);
                    const bottom = Math.min(...outline.map(q => q.y));
                    const cx = outline.reduce((s, q) => s + q.x, 0) / outline.length;
                    return (
                        <g key={p.id} className="obench-part" data-class={CLASS[p.kind] ?? 'glass'} data-selected={p.id === selectedId}
                            onPointerDown={e => startMove(e, p)}>
                            {/* A lamp glows in its own light's colour. */}
                            <polygon points={outline.map(S).join(' ')} vectorEffect="non-scaling-stroke"
                                style={CLASS[p.kind] === 'source' ? { fill: sourceInk(p) } : undefined} />
                            {CLASS[p.kind] === 'mirror' && <path className="hatch" d={mirrorHatch(p)} vectorEffect="non-scaling-stroke" />}
                            {CLASS[p.kind] === 'housing' && (() => {
                                // The slit: the housing's front face, lit in the light's colour.
                                const face = fromAngle(p.angle);
                                const reach = (q: Vec2): number => (q.x - p.at.x) * face.x + (q.y - p.at.y) * face.y;
                                const front = [...outline].sort((a, b) => reach(b) - reach(a)).slice(0, 2);
                                return (
                                    <line className="slit" x1={front[0].x} y1={Y(front[0].y)} x2={front[1].x} y2={Y(front[1].y)}
                                        style={{ stroke: sourceInk(p) }} vectorEffect="non-scaling-stroke" />
                                );
                            })()}
                            {/* The finger's target: the outline, fattened by a stroke 24px
                                wide on screen. In scene units rather than a non-scaling
                                stroke — WebKit does not hit-test a non-scaling stroke
                                where it draws it, so on an iPhone no lens or mirror
                                could be tapped. */}
                            <polygon className="hit" points={outline.map(S).join(' ')} style={{ strokeWidth: px(HIT_PX) }} />
                            <text className="name" data-hue={keys.get(p.id)?.hue} x={cx} y={Y(bottom) + px(16)} textAnchor="middle">{defOf(p).name(p, world)}</text>
                        </g>
                    );
                })}

                {/* Rays: real light solid; the backward extensions that locate a
                    virtual image dashed, because they carry no light. While the
                    animation plays, each ray is drawn only as far as the light
                    has got, with a head on its moving tip. */}
                <g className="obench-rays">
                    {analysis.rays.map(ray => {
                        const colour = rayColour(ray);
                        const vs = ray.vertices;
                        const fullPath = drawnPath(ray);
                        if (animating) {
                            const cut = truncatePath(fullPath, travel as number);
                            return (
                                <g key={ray.id} data-role={ray.role} style={{ color: colour }}>
                                    <polyline points={cut.points.map(S).join(' ')} vectorEffect="non-scaling-stroke" />
                                    {cut.tipDir && <polygon className="head" points={arrowHead(cut.points[cut.points.length - 1], cut.tipDir)} />}
                                </g>
                            );
                        }
                        const path = fullPath;
                        const pts = path.map(S).join(' ');
                        const img = imageOfSource(ray);
                        const lastSurface = ray.ended === 'left-the-bench' ? vs[vs.length - 2] : undefined;
                        // Where the last straight run is drawn from — on the arc, for a mirror.
                        const lastFrom = path[path.length - 2];
                        const firstDir = vs[0].direction;
                        const arrowAt = add(vs[0].at, scale(firstDir, Math.min(dist(vs[0].at, vs[1]?.at ?? vs[0].at) / 2, px(60))));
                        return (
                            <g key={ray.id} data-role={ray.role} style={{ color: colour }}>
                                <polyline points={pts} vectorEffect="non-scaling-stroke" />
                                <polygon className="head" points={arrowHead(arrowAt, firstDir)} />
                                {img?.kind === 'virtual' && lastSurface?.surfaceId && (
                                    <line className="virtual" x1={lastFrom.x} y1={Y(lastFrom.y)} x2={img.at.x} y2={Y(img.at.y)} vectorEffect="non-scaling-stroke" />
                                )}
                            </g>
                        );
                    })}
                    {/* White light: where its three colours still run together, one
                        white ray over them; where a prism has parted them, each
                        shows in its own colour from underneath. */}
                    {whiteRuns.map(run => {
                        const vs = run.vertices;
                        const path = drawnPath({ ...run.ray, vertices: vs });
                        const style = { color: whiteInk(props.theme) };
                        if (animating) {
                            const cut = truncatePath(path, travel as number);
                            return (
                                <g key={`white-${run.ray.id}`} data-role={run.ray.role} style={style}>
                                    <polyline points={cut.points.map(S).join(' ')} vectorEffect="non-scaling-stroke" />
                                    {/* Its head only while the light is still white; past the prism the colours carry their own. */}
                                    {cut.tipDir && !cut.done && (
                                        <polygon className="head" points={arrowHead(cut.points[cut.points.length - 1], cut.tipDir)} />
                                    )}
                                </g>
                            );
                        }
                        const firstDir = vs[0].direction;
                        const arrowAt = add(vs[0].at, scale(firstDir, Math.min(dist(vs[0].at, run.ray.vertices[1]?.at ?? vs[0].at) / 2, px(60))));
                        return (
                            <g key={`white-${run.ray.id}`} data-role={run.ray.role} style={style}>
                                <polyline points={path.map(S).join(' ')} vectorEffect="non-scaling-stroke" />
                                <polygon className="head" points={arrowHead(arrowAt, firstDir)} />
                            </g>
                        );
                    })}
                </g>

                {/* Normals and angles, for a ray box's ray: the lab-report picture. */}
                {/* While the animation plays, a surface's normal and angles appear the
                    moment the light reaches it, not all at the end. */}
                {showAngles && (
                    <g className="obench-angles">
                        {analysis.rays.filter(r => r.role === 'single' && r.source.point === 'ray' && (!r.white || r.wavelength === optics.WHITE_LEAD)).flatMap(ray => {
                            const reached: number[] = [0];
                            for (let k = 1; k < ray.vertices.length; k++) {
                                reached.push(reached[k - 1] + dist(ray.vertices[k - 1].at, ray.vertices[k].at));
                            }
                            return ray.vertices.map((v, i) => {
                                if (v.incidence === undefined || i === 0) return null;
                                if (animating && (travel as number) < reached[i]) return null;
                                const n = normalAt(v);
                                if (!n) return null;
                                const L = px(34);
                                const inDir = ray.vertices[i - 1].direction;
                                const iAt = add(v.at, scale(inDir, -px(46)));
                                const rAt = add(v.at, scale(v.direction, px(46)));
                                return (
                                    <g key={`${ray.id}-${i}`}>
                                        <line x1={v.at.x - n.x * L} y1={Y(v.at.y - n.y * L)} x2={v.at.x + n.x * L} y2={Y(v.at.y + n.y * L)} vectorEffect="non-scaling-stroke" />
                                        <text x={iAt.x} y={Y(iAt.y)} textAnchor="middle">i {v.incidence.toFixed(1)}°</text>
                                        {v.refraction !== undefined && !v.tir && (
                                            <text x={rAt.x} y={Y(rAt.y)} textAnchor="middle">r {v.refraction.toFixed(1)}°</text>
                                        )}
                                        {v.tir && <text x={rAt.x} y={Y(rAt.y)} textAnchor="middle">TIR</text>}
                                    </g>
                                );
                            });
                        })}
                    </g>
                )}

                {/* Objects, and their images. */}
                {world.parts.filter(p => p.kind === 'object').map(p => {
                    const data = p.data as unknown as O.ObjectData;
                    const A = objectPoint(p as O.Part<O.ObjectData>, 0);
                    const B = objectPoint(p as O.Part<O.ObjectData>, 1);
                    const report = reports.get(p.id);
                    const rod = data.form === 'rod';
                    const gid = `ob-rod-${p.id}`;
                    const dir = unit(sub(B, A));
                    // The arrow, its rays and its image share its one colour.
                    const ink = rod ? undefined : wavelengthColour(optics.wavelengthOf(p, world));
                    return (
                        <g key={p.id} className="obench-object" data-form={data.form} data-selected={p.id === selectedId} onPointerDown={e => startMove(e, p)}>
                            {rod && (
                                <linearGradient id={gid} gradientUnits="userSpaceOnUse" x1={A.x} y1={Y(A.y)} x2={B.x} y2={Y(B.y)}>
                                    {[0, 0.25, 0.5, 0.75, 1].map(t => <stop key={t} offset={t} stopColor={rodColour(data.palette, t)} />)}
                                </linearGradient>
                            )}
                            <line className="body" x1={A.x} y1={Y(A.y)} x2={B.x} y2={Y(B.y)}
                                style={{ stroke: rod ? `url(#${gid})` : ink }} vectorEffect="non-scaling-stroke" />
                            {/* Scene units, not a non-scaling stroke: see the parts' hit. */}
                            <line className="hit" x1={A.x} y1={Y(A.y)} x2={B.x} y2={Y(B.y)} style={{ strokeWidth: px(HIT_PX) }} />
                            {!rod && <polygon className="head" points={arrowHead(B, dir, 10)} style={{ fill: ink }} />}
                            {rod && <>
                                <text className="end" x={A.x - px(10)} y={Y(A.y) + px(4)} textAnchor="end">A</text>
                                <text className="end" x={B.x - px(10)} y={Y(B.y) + px(4)} textAnchor="end">B</text>
                            </>}
                            <text className="name" x={A.x} y={Y(A.y) + px(34)} textAnchor="middle">{defOf(p).name(p, world)}</text>
                            {report && !animating && <ObjectImage report={report} data={data} px={px} Y={Y} arrowHead={arrowHead} id={p.id} ink={ink} />}
                        </g>
                    );
                })}

                {/* A point source's image: a dot, hollow when virtual. */}
                {!animating && world.parts.filter(p => p.kind === 'pointSource').map(p => {
                    const img = reports.get(p.id)?.points[0];
                    if (!img || (img.kind !== 'real' && img.kind !== 'virtual')) return null;
                    const c = wavelengthColour(optics.wavelengthOf(p, world));
                    return <circle key={p.id} className="obench-pointimage" data-kind={img.kind} cx={img.at.x} cy={Y(img.at.y)} r={px(4)}
                        style={{ stroke: c, fill: img.kind === 'virtual' ? undefined : c }} vectorEffect="non-scaling-stroke" />;
                })}

                {/* Turning handle, for a free part that turns. */}
                {selected && selected.mount === 'free' && defOf(selected).freedom.turns && (() => {
                    const outline = defOf(selected).outline(selected, world);
                    const R = Math.max(...outline.map(q => dist(q, selected.at)));
                    const h = add(selected.at, scale(fromAngle(selected.angle), R + px(26)));
                    return (
                        <g className="obench-handle" onPointerDown={e => startTurn(e, selected)}>
                            <line x1={selected.at.x} y1={Y(selected.at.y)} x2={h.x} y2={Y(h.y)} vectorEffect="non-scaling-stroke" />
                            {/* A finger's target, invisible, round the mouse-sized knob. */}
                            <circle className="hit" cx={h.x} cy={Y(h.y)} r={px(22)} />
                            <circle className="grip" cx={h.x} cy={Y(h.y)} r={px(7)} vectorEffect="non-scaling-stroke" />
                        </g>
                    );
                })()}

                {live?.snap.guides.map((g, i) => (
                    <line key={i} className="obench-guide" x1={g.from.x} y1={Y(g.from.y)} x2={g.to.x} y2={Y(g.to.y)} vectorEffect="non-scaling-stroke" />
                ))}
            </svg>

            {/* The light, traced out from its sources at one view width per 3 s. */}
            <button
                type="button" className="obench-play" aria-pressed={animating}
                disabled={!animating && analysis.rays.length === 0}
                onClick={() => (animating ? stopAnimation() : playRays())}
            >{animating ? '■ Stop' : '▶ Play ray animation'}</button>

            {/* Off the view: a tag at the edge, pointing the way; click to bring it in. */}
            {tags.map(t => {
                const spot = edgeSpot(t.toward);
                return (
                    <button key={t.key} type="button" className="obench-edgetag" style={{ left: spot.left, top: spot.top }}
                        onClick={() => props.onReveal(t.at)}>
                        {t.text} {spot.arrow}
                    </button>
                );
            })}

            {live && (() => {
                const part = partById.get(live.id);
                if (!part) return null;
                const r = svgRef.current?.getBoundingClientRect();
                const at = live.snap.at;
                return (
                    <div className="obench-live" style={{ left: live.client.x - (r?.left ?? 0) + 14, top: live.client.y - (r?.top ?? 0) - 30 }}>
                        {defOf(part).name(part, world)}: x = {at.x.toFixed(1)} cm
                        {part.mount === 'free' && <>, y = {at.y.toFixed(1)} cm</>}
                        {live.snap.to && <b> · {live.snap.to}</b>}
                        {freeDrag && <b> · free</b>}
                    </div>
                );
            })()}
        </div>
    );
}

/** The image of an object: each piece drawn with the rod's colours carried across point by point. */
function ObjectImage({ report, data, px, Y, arrowHead, id, ink }: {
    report: O.ImageReport;
    data: O.ObjectData;
    px: (n: number) => number;
    Y: (y: number) => number;
    arrowHead: (at: Vec2, dir: Vec2, sizePx?: number) => string;
    id: string;
    /** The arrow's colour; undefined for a rod, which carries its gradient. */
    ink?: string;
}): React.JSX.Element {
    const rod = data.form === 'rod';
    return (
        <g className="obench-image" data-form={data.form}>
            {report.pieces.map((pc, k) => {
                const pts = report.points.slice(pc.from, pc.to + 1);
                const first = pts[0].at, last = pts[pts.length - 1].at;
                const span = sub(last, first);
                const L2 = span.x * span.x + span.y * span.y;
                const virtual = pts[0].kind === 'virtual';
                const gid = `ob-img-${id}-${k}`;
                const b = pts.find(p => p.t === 1);
                return (
                    <g key={k} data-kind={virtual ? 'virtual' : 'real'}>
                        {rod && L2 > 1e-12 && (
                            <linearGradient id={gid} gradientUnits="userSpaceOnUse" x1={first.x} y1={Y(first.y)} x2={last.x} y2={Y(last.y)}>
                                {/* Stops where each sample actually imaged — not spread evenly —
                                    so a tilted rod's image is bunched where the physics bunches it. */}
                                {pts.map((p, i) => {
                                    const off = ((p.at.x - first.x) * span.x + (p.at.y - first.y) * span.y) / L2;
                                    return <stop key={i} offset={Math.min(1, Math.max(0, off))} stopColor={rodColour(data.palette, p.t)} />;
                                })}
                            </linearGradient>
                        )}
                        <polyline className="body" points={pts.map(p => `${p.at.x},${Y(p.at.y)}`).join(' ')}
                            style={{ stroke: rod ? (L2 > 1e-12 ? `url(#${gid})` : rodColour(data.palette, pts[0].t)) : ink }}
                            vectorEffect="non-scaling-stroke" />
                        {!rod && b && pts.length > 1 && (
                            <polygon className="head" points={arrowHead(b.at, unit(sub(b.at, pts[pts.length - 2].at)), 10)} style={{ fill: ink }} />
                        )}
                        {rod && pts.some(p => p.t === 0) && (() => {
                            const a = pts.find(p => p.t === 0) as O.SampledImage;
                            return <text className="end" x={a.at.x + px(10)} y={Y(a.at.y) + px(4)}>A′</text>;
                        })()}
                        {rod && b && <text className="end" x={b.at.x + px(10)} y={Y(b.at.y) + px(4)}>B′</text>}
                    </g>
                );
            })}
        </g>
    );
}
